/**
 * Design Lab Init Machine Actions
 * All action implementations for the state machine
 */

import { assign } from "xstate";

import { parseExternalUploads } from "@design-lab/shared/lib";
import type {
  ParsedUrlParams,
  Composition,
  PdpQuantityInfo,
  UploadSource,
  DesignLabInitContext,
  DesignLabInitEvent,
  DoneInvokeProjectEvent,
  DoneInvokeLatestProjectEvent,
  DoneInvokeDownloadFilesEvent,
  ErrorInvokeEvent
} from "./design-lab-init.types";

// Helper function to extract UUID from path
function extractUuidFromPath(pathname: string): string | undefined {
  const match = pathname.match(/\/design-lab\/([a-f0-9-]+)/);
  return match?.[1];
}

// Helper function to parse PDP size data
function parsePdpSizeData(
  jsonString: string | undefined
): Record<string, PdpQuantityInfo> | undefined {
  if (!jsonString) return undefined;

  try {
    return JSON.parse(jsonString);
  } catch {
    return undefined;
  }
}

export const actions = {
  // URL Parsing
  parseUrlParams: assign(() => {
    const params = new URLSearchParams(window.location.search);
    const { pathname } = window.location;

    const asgid = params.get("asgid");
    const uuid = extractUuidFromPath(pathname);
    const choice = params.get("choice");
    const artworkUrls = params.get("artwork_urls");
    const uploadUrls = params.get("upload_urls");
    const fileContentType = params.get("file_content_type");
    const side = params.get("side");
    const catalogNumber = params.get("catalog_number");
    const ac = params.get("ac");
    const pdpSizeData = params.get("pdp_size_data");
    const sample = params.get("sample") === "true";

    const urlParams: ParsedUrlParams = {
      ...(asgid && { asgid }),
      ...(uuid && { uuid }),
      ...(choice && { choice: choice as "Library" | "Canva" }),
      ...(artworkUrls && { artworkUrls }),
      ...(uploadUrls && { uploadUrls }),
      ...(fileContentType && { fileContentType }),
      side: (side as "front" | "back") || "front",
      ...(catalogNumber && { catalogNumber }),
      ...(ac && { ac }),
      ...(pdpSizeData && { pdpSizeData }),
      sample
    };

    // Calculate composition from the parsed params
    let composition: Composition | null = null;
    const parsedPdpData = parsePdpSizeData(pdpSizeData || undefined);

    if (!catalogNumber || !ac) {
      // Use defaults for create flows without composition params
      const needsDefaults = choice || artworkUrls || uploadUrls;

      if (needsDefaults) {
        composition = {
          catalogNumber: "G640",
          ac: "White",
          ...(parsedPdpData && { pdpSizeData: parsedPdpData })
        };
      }
    } else {
      composition = {
        catalogNumber,
        ac,
        ...(parsedPdpData && { pdpSizeData: parsedPdpData })
      };
    }

    // Calculate upload source
    let uploadSource: UploadSource | null = null;
    if (artworkUrls || uploadUrls) {
      uploadSource = "external";
    } else if (choice === "Library") {
      uploadSource = "library";
    } else if (choice === "Canva") {
      uploadSource = "canva";
    }

    // Calculate target side
    const targetSide = (side as "front" | "back") || "front";

    // Calculate external uploads
    const externalUploads = parseExternalUploads(artworkUrls || uploadUrls || undefined);
    const externalUrls = externalUploads?.map(upload => upload.url);

    return {
      urlParams,
      composition,
      uploadSource,
      targetSide,
      externalUploads,
      externalUrls
    };
  }),

  // Flow Type Actions
  setEditMode: assign({
    isEditMode: true,
    flowType: null
  }),

  setFlowTypeExisting: assign({
    flowType: "existing"
  }),

  setFlowTypeCreate: assign({
    flowType: "create"
  }),

  setFlowTypeModal: assign({
    flowType: "modal"
  }),

  // Project Actions
  setProject: assign({
    project: ({ event }: { event: DoneInvokeProjectEvent }) => event.output
  }),

  navigateToProjectUrl: ({ context }: { context: DesignLabInitContext }) => {
    const { project, navigate, isEditMode } = context;

    if (project?.uuid && navigate && !isEditMode) {
      const newUrl = `/design-lab/${project.uuid}`;
      const currentPath = window.location.pathname;

      // Only navigate if we're not already on the UUID URL
      if (currentPath !== newUrl) {
        navigate(newUrl, { replace: true });
      }
    }
  },

  setLatestProject: assign({
    latestProject: ({ event }: { event: DoneInvokeLatestProjectEvent }) => event.output
  }),

  // Upload Actions
  prepareProjectCreation: assign({
    externalUploads: ({ context }: { context: DesignLabInitContext }) => {
      if (context.uploadSource === "external") {
        const urls = context.urlParams.artworkUrls || context.urlParams.uploadUrls;
        return parseExternalUploads(urls);
      }
      return undefined;
    },
    externalUrls: ({ context }: { context: DesignLabInitContext }) => {
      if (context.uploadSource === "external") {
        const urls = context.urlParams.artworkUrls || context.urlParams.uploadUrls;
        const uploads = parseExternalUploads(urls);
        return uploads?.map(upload => upload.url);
      }
      return undefined;
    }
  }),

  setUploadedFiles: assign({
    uploadedFiles: ({
      event
    }: {
      event: DoneInvokeDownloadFilesEvent | Extract<DesignLabInitEvent, { type: "FILES_SELECTED" }>;
    }) => {
      if (event.type === "FILES_SELECTED") {
        return event.files;
      }
      return event.output;
    }
  }),

  // Error Actions
  setError: assign({
    error: ({ event }: { event: ErrorInvokeEvent }) => event.error
  }),

  clearError: assign({
    error: undefined
  }),

  // Reset Action - explicitly clear all properties (XState's assign merges, not replaces)
  resetContext: assign(
    ({ context }: { context: DesignLabInitContext }) =>
      ({
        urlParams: {},
        isEditMode: false,
        flowType: null,
        project: null,
        latestProject: null,
        composition: null,
        uploadSource: null,
        targetSide: "front" as const,
        showModal: false,
        modalAction: null,
        error: undefined,
        externalUploads: undefined,
        externalUrls: undefined,
        uploadedFiles: undefined,
        ...(context.apolloClient && { apolloClient: context.apolloClient }),
        ...(context.navigate && { navigate: context.navigate })
      }) as unknown as DesignLabInitContext
  )
};
